import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Paper, Typography, CircularProgress, Button } from '@mui/material';
import fileService from '../../services/fileService';
import { useSnackbar } from 'notistack';
import PreviewEngine, { PreviewEngineHandle } from '../../preview/PreviewEngine';
import PreviewToolbar from './PreviewToolbar';
import {
  getPreviewMimeType,
  getPreviewCategory,
  extensionOf,
  type PreviewCategory,
} from '../../utils/previewUtils';

interface FilePreviewProps {
  fileId: string;
  versionId?: string;
  onClose?: () => void;
}

type FileDetails = {
  id: string;
  name: string;
  size: number;
  mimetype: string;
  extension: string;
  version?: number;
};

export const FilePreview: React.FC<FilePreviewProps> = ({ fileId, versionId }) => {
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState<FileDetails | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [pdfScale, setPdfScale] = useState(1);
  const [category, setCategory] = useState<PreviewCategory>('unsupported');
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [textFontSize, setTextFontSize] = useState(14);
  const [textWordWrap, setTextWordWrap] = useState(true);
  const engineRef = useRef<PreviewEngineHandle>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const loadFile = async () => {
      if (!fileId) return;
      try {
        setLoading(true);
        setPreviewError(null);
        const fileDetails = await fileService.getFile(fileId);
        setFile({
          id: fileDetails.id,
          name: fileDetails.name,
          size: fileDetails.size || 0,
          mimetype: String(fileDetails.mimetype || fileDetails.mimeType || 'application/octet-stream'),
          extension: fileDetails.extension || fileDetails.name.split('.').pop()?.toLowerCase() || '',
          version: fileDetails.version as number | undefined,
        });
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
            ?.message ||
          (err as Error)?.message ||
          'Failed to load file';
        setPreviewError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadFile();
  }, [fileId, enqueueSnackbar]);

  const handleDownload = async () => {
    try {
      if (versionId) await fileService.downloadFileVersion(fileId, versionId);
      else await fileService.downloadFile(fileId);
    } catch (err: unknown) {
      enqueueSnackbar((err as Error)?.message || 'Download failed', { variant: 'error' });
    }
  };

  const handlePrint = useCallback(async () => {
    try {
      const previewUrl =
        mediaUrl ||
        (await fileService.buildPreviewUrl(
          fileId,
          undefined,
          { mode: 'preview', optimize: true },
          { withAccessToken: true, versionId }
        ));
      const printWindow = window.open(previewUrl, '_blank');
      if (printWindow) printWindow.onload = () => printWindow.print();
      else throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
    } catch (err: unknown) {
      enqueueSnackbar((err as Error)?.message || 'Print failed', { variant: 'error' });
    }
  }, [fileId, versionId, mediaUrl, enqueueSnackbar]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Loading preview...</Typography>
      </Box>
    );
  }

  if (previewError || !file) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Typography color="error" variant="h6">{previewError || 'Could not load file'}</Typography>
        <Button variant="outlined" onClick={() => engineRef.current?.retry()}>Retry</Button>
      </Box>
    );
  }

  const mime = getPreviewMimeType(file);
  const resolvedCategory =
    category !== 'unsupported' ? category : getPreviewCategory(mime, extensionOf(file.name));

  return (
    <Paper elevation={3} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PreviewToolbar
        variant="compact"
        category={resolvedCategory}
        fileName={file.name}
        imageZoom={imageZoom}
        imageRotation={imageRotation}
        pdfScale={pdfScale}
        textWordWrap={textWordWrap}
        textFontSize={textFontSize}
        onImageZoomChange={setImageZoom}
        onImageRotationChange={setImageRotation}
        onImageReset={() => { setImageZoom(1); setImageRotation(0); }}
        onPdfScaleChange={setPdfScale}
        onPdfFit={() => setPdfScale(1)}
        onDownload={handleDownload}
        onPrint={handlePrint}
        onOpenNewTab={mediaUrl ? () => window.open(mediaUrl, '_blank') : undefined}
        onCopyText={() => {
          const text = engineRef.current?.getTextContent();
          if (text) navigator.clipboard.writeText(text);
        }}
        onTextWordWrapToggle={() => setTextWordWrap((w) => !w)}
        onTextFontSizeChange={setTextFontSize}
        onRetry={() => engineRef.current?.retry()}
        showRetry
      />
      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
        <PreviewEngine
          ref={engineRef}
          file={{ id: file.id, name: file.name, size: file.size, mimetype: file.mimetype }}
          versionId={versionId}
          imageZoom={imageZoom}
          imageRotation={imageRotation}
          pdfScale={pdfScale}
          textFontSize={textFontSize}
          textWordWrap={textWordWrap}
          compact
          onCategoryChange={setCategory}
          onMediaUrlChange={setMediaUrl}
        />
      </Box>
    </Paper>
  );
};

export default FilePreview;
